import { doc, getDoc, getDocFromCache, type DocumentSnapshot } from "firebase/firestore";
import { db } from "../../../firebase/firebase";

const WEB_REQUEST_COMMAND = "execute_web_request";
const DEFAULT_TIMEOUT_MS = 6000;

export const WEB_REQUEST_ACTIONS = {
  emergency: "emergency_notification_settings_sync",
  bbs: "bbs_notification_settings_sync",
  mimamori: "mimamori_settings_sync",
} as const;

export type RecipientMode = "all" | "custom";
export type MimamoriNotifyLevel = "disabled" | "critical" | "suspicious" | "all";

export interface NotificationRecipient {
  email: string;
  isManager: boolean;
}

export interface EmailNotificationSettings {
  mode: RecipientMode;
  selectedEmails: string[];
}

export interface MimamoriNotificationSettings {
  notifyLevel: MimamoriNotifyLevel;
  selectedEmails: string[];
}

export function normalizeEmail(email: string | null | undefined): string {
  return (email ?? "").trim().toLowerCase();
}

export function recipientLabel(recipient: NotificationRecipient): string {
  return recipient.isManager ? "管理者" : "親族・知人";
}

export function buildNotificationRecipients(
  managerEmail: string | null | undefined,
  familyEmails: string[] | null | undefined
): NotificationRecipient[] {
  const manager = normalizeEmail(managerEmail);
  const recipients: NotificationRecipient[] = [];
  const seen = new Set<string>();

  if (manager) {
    recipients.push({ email: manager, isManager: true });
    seen.add(manager);
  }

  for (const raw of familyEmails ?? []) {
    const email = normalizeEmail(raw);
    if (!email || seen.has(email)) continue;
    seen.add(email);
    recipients.push({ email, isManager: false });
  }
  return recipients;
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error("Firestore の読み取りがタイムアウトしました"));
    }, timeoutMs);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(timer);
        reject(err);
      }
    );
  });
}

async function readCachedDevice(deviceId: string): Promise<DocumentSnapshot | null> {
  try {
    return await getDocFromCache(doc(db, "devices", deviceId));
  } catch {
    return null;
  }
}

async function readDeviceDocument(
  deviceId: string,
  timeoutMs = DEFAULT_TIMEOUT_MS
): Promise<DocumentSnapshot> {
  const ref = doc(db, "devices", deviceId);
  try {
    return await withTimeout(getDoc(ref), timeoutMs);
  } catch (err) {
    const cached = await readCachedDevice(deviceId);
    if (cached?.exists()) return cached;
    throw err;
  }
}

export function snapshotEmailSettings(mode: RecipientMode, selectedEmails: string[]): string {
  return JSON.stringify({
    mode,
    selectedEmails: [...selectedEmails].map(normalizeEmail).filter(Boolean).sort(),
  });
}

export function snapshotMimamoriSettings(
  notifyLevel: MimamoriNotifyLevel,
  selectedEmails: string[]
): string {
  return JSON.stringify({
    notifyLevel,
    selectedEmails: [...selectedEmails].map(normalizeEmail).filter(Boolean).sort(),
  });
}

export async function loadEmergencyNotificationSettings(
  deviceId: string
): Promise<EmailNotificationSettings> {
  const snap = await readDeviceDocument(deviceId);
  if (!snap.exists()) throw new Error("デバイスが存在しません");
  const raw = snap.data().emergencyEmailNotification as { mode?: unknown; selectedEmails?: unknown } | undefined;
  const selectedEmailsRaw = raw?.selectedEmails;
  return {
    mode: raw?.mode === "custom" ? "custom" : "all",
    selectedEmails: Array.isArray(selectedEmailsRaw)
      ? selectedEmailsRaw
          .filter((v): v is string => typeof v === "string")
          .map(normalizeEmail)
          .filter(Boolean)
      : [],
  };
}

export async function loadBbsNotificationSettings(
  deviceId: string
): Promise<EmailNotificationSettings> {
  const snap = await readDeviceDocument(deviceId);
  if (!snap.exists()) throw new Error("デバイスが存在しません");
  const raw = snap.data().bbsEmailNotification as { mode?: unknown; selectedEmails?: unknown } | undefined;
  const selectedEmailsRaw = raw?.selectedEmails;
  return {
    mode: raw?.mode === "custom" ? "custom" : "all",
    selectedEmails: Array.isArray(selectedEmailsRaw)
      ? selectedEmailsRaw
          .filter((v): v is string => typeof v === "string")
          .map(normalizeEmail)
          .filter(Boolean)
      : [],
  };
}

export async function loadMimamoriNotificationSettings(
  deviceId: string
): Promise<MimamoriNotificationSettings> {
  const snap = await readDeviceDocument(deviceId);
  if (!snap.exists()) throw new Error("デバイスが存在しません");
  const raw = snap.data().mimamoriEmailNotification as
    | { notifyLevel?: unknown; selectedEmails?: unknown }
    | undefined;
  const notifyLevel =
    raw?.notifyLevel === "critical" ||
    raw?.notifyLevel === "suspicious" ||
    raw?.notifyLevel === "all" ||
    raw?.notifyLevel === "disabled"
      ? raw.notifyLevel
      : "disabled";
  const selectedEmailsRaw = raw?.selectedEmails;
  return {
    notifyLevel,
    selectedEmails: Array.isArray(selectedEmailsRaw)
      ? selectedEmailsRaw
          .filter((v): v is string => typeof v === "string")
          .map(normalizeEmail)
          .filter(Boolean)
      : [],
  };
}

export { WEB_REQUEST_COMMAND };
